import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SetupSignals } from '../shared/helpers/guided-setup.helper';
import { OutgoingNumberDTO } from '../shared/models/callback-on-callnote.model';

export interface GuidedSetupDialogData {
	signals: SetupSignals;
	availableNumbers: OutgoingNumberDTO[];
	/** Formatted from the payload's price and currency; null when no figure may be shown. */
	monthlyCost: string | null;
	sipOnOffer: boolean;
	/** PS-6646: the callnote's phone service; a sign-in for any other one is refused. */
	expectedPhoneServiceId: number | null;
	/** The customer's language id from the callnote payload; sets the dialog's `lang` (M17). */
	languageId?: number | null;
}

/**
 * The id of the heading every phase renders (`guided-setup.html`). The dialog config points
 * `ariaLabelledBy` at it, so the dialog is announced by the current step's title (M8).
 */
export const GUIDED_SETUP_HEADING_ID = 'guided-setup-heading';

/**
 * The overlay pane's class. Global styles key on it (`styles.scss`), because the pane lives in the
 * CDK overlay outside every component's encapsulation.
 */
export const GUIDED_SETUP_PANEL_CLASS = 'guided-setup-dialog';

/**
 * The panel's DEFINITE width; `maxWidth: 92vw` shrinks it on small screens. Paired with the wide-
 * and-short tier in `styles.scss` and the breakpoint tokens in `shared/styles/variables.scss`.
 */
export const GUIDED_SETUP_DIALOG_WIDTH = '760px';

/** Platform language ids to BCP 47 tags. */
const LANGUAGE_TAGS: Readonly<Record<number, string>> = {
	1: 'de',
	2: 'en',
	3: 'it',
	4: 'fr',
	6: 'es',
	9: 'nl',
};

/**
 * The `lang` for the dialog's content, or null for an unknown id - then the page's own `lang`
 * applies rather than a guessed one.
 */
export function languageTagFor(languageId: number | null | undefined): string | null {
	return languageId == null ? null : (LANGUAGE_TAGS[languageId] ?? null);
}

export type GuidedSetupResult =
	/** Setup finished; the parent creates the callback with this number, without a further click. */
	| { outcome: 'ready'; outgoingNumber: string }
	/** The customer gave up on the run. */
	| { outcome: 'dismissed' }
	/** Closed, run kept: pressing the call button again resumes at the same step. */
	| { outcome: 'suspended' }
	| { outcome: 'failed'; reason: string }
	/**
	 * The run ends with the service following up: the parent creates the callback without a number
	 * and with `requestContactForBookingNumber` set.
	 */
	| { outcome: 'contactService' };

/**
 * PS-6495 — the dialog that frames the workflow.
 *
 * A thin host on purpose. All behaviour stays in `GuidedSetup`, which is why its twenty-odd
 * state-machine tests still exercise the real thing; this class only turns its outputs into a
 * dialog result. The state itself lives in `GuidedSetupStore`, outside both of them, so closing
 * this dialog never throws a half-finished run away.
 *
 * Opened with `disableClose: true` since 2026-09-11: the backdrop and Escape no longer close it.
 * This run concludes a paid contract, so leaving it is a decision the customer makes with a button.
 * The header's close button and "Später" both call `suspend()`, which resolves to `suspended` and
 * keeps the run — reopening lands on the same step it was left at, which is the behaviour Torsten
 * asked for on 2026-09-04 (a cancelled dialog should just reopen at the current state).
 */
@Component({
	selector: 'app-guided-setup-dialog',
	templateUrl: './guided-setup-dialog.html',
	standalone: false,
	changeDetection: ChangeDetectionStrategy.OnPush,
	/**
	 * M17: the copy is in the customer's language while `index.html` says `en`, so screen readers
	 * read it with the wrong voice. Set on the dialog's own root; the page around it is untouched.
	 */
	host: { '[attr.lang]': 'lang' },
})
export class GuidedSetupDialog {
	public readonly data: GuidedSetupDialogData = inject(MAT_DIALOG_DATA);

	public readonly lang = languageTagFor(this.data.languageId);

	private readonly dialogRef: MatDialogRef<GuidedSetupDialog, GuidedSetupResult> =
		inject(MatDialogRef);

	onReady(outgoingNumber: string): void {
		this.dialogRef.close({ outcome: 'ready', outgoingNumber });
	}

	onDismissed(): void {
		this.dialogRef.close({ outcome: 'dismissed' });
	}

	onSuspended(): void {
		this.dialogRef.close({ outcome: 'suspended' });
	}

	onFailed(reason: string): void {
		this.dialogRef.close({ outcome: 'failed', reason });
	}

	onContactService(): void {
		this.dialogRef.close({ outcome: 'contactService' });
	}
}
